import { type ChangeEvent, useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import type { PayloadJson, Stage } from "./types";
import { clearGroup, createGroundPlane, createLine, createMesh, createPolygon } from "./utils";

const COLORS = {
	CheckZone: 0x4caf50,
	BuildingZone: 0x2196f3,
	ExcludedZone: 0xf44336,
	BuildingLine: 0xffc107,
	Surrounding: 0x9e9e9e,
} as const;

const buildScene = (stage: Stage, json: PayloadJson): THREE.Mesh | null => {
	const { root, camera, controls } = stage;

	clearGroup(root);

	const projectData = json.Input?.ProjectData ?? null;
	const inputModel = json.Input?.InputModel ?? null;

	if (projectData === null || inputModel === null) {
		console.error({ json });
		throw new Error("Incorrect JSON shape");
	}

	(inputModel.CheckZones ?? []).forEach((zone) => {
		const points = zone.Boundary?.Points ?? null;

		if (points !== null) {
			const polygon = createPolygon(points, COLORS.CheckZone, 0.15);

			if (polygon !== null) {
				root.add(polygon);
			}
		}
	});

	(inputModel.BuildingZones ?? []).forEach((zone) => {
		const points = zone.Boundary?.Points ?? null;

		if (points !== null) {
			const polygon = createPolygon(points, COLORS.BuildingZone, 0.3);

			if (polygon !== null) {
				root.add(polygon);
			}
		}
	});

	(inputModel.ExcludedZones ?? []).forEach(({ Boundary }) => {
		const points = Boundary?.Points ?? null;

		if (points !== null) {
			const polygon = createPolygon(points, COLORS.ExcludedZone, 0.3);

			if (polygon !== null) {
				root.add(polygon);
			}
		}
	});

	(inputModel.BuildingLines ?? []).forEach(({ Shape }) => {
		const points = Shape?.Points ?? null;

		if (points !== null) {
			const line = createLine(points, COLORS.BuildingLine);

			if (line !== null) {
				root.add(line);
			}
		}
	});

	(inputModel.Surroundings ?? []).forEach(({ Mesh }) => {
		if (Mesh !== null) {
			const mesh = createMesh(Mesh, COLORS.Surrounding);

			if (mesh !== null) {
				root.add(mesh);
			}
		}
	});

	const box = new THREE.Box3().setFromObject(root);
	if (box.isEmpty()) {
		return null;
	}

	const center = box.getCenter(new THREE.Vector3());
	const size = box.getSize(new THREE.Vector3()).length();

	controls.target.copy(center);
	camera.position.copy(center).add(new THREE.Vector3(size, size, size).multiplyScalar(0.6));
	camera.near = size / 1000;
	camera.far = size * 100;
	camera.updateProjectionMatrix();

	const screenshotDataUrl = json._debug?.screenshot;
	const mapState = json._debug?.mapState;
	const locationOnEarth = projectData.LocationOnEarth;

	if (screenshotDataUrl === undefined || mapState === undefined || locationOnEarth?.Longitude === undefined || locationOnEarth?.Latitude === undefined) {
		return null;
	}

	const groundPlane = createGroundPlane({
		screenshotDataUrl,
		mapState,
		projectCenter: [locationOnEarth.Longitude, locationOnEarth.Latitude],
	});
	root.add(groundPlane);
	return groundPlane;
};

const PayloadViewer = (): React.ReactNode => {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const stageRef = useRef<Stage | null>(null);
	const groundPlaneRef = useRef<THREE.Mesh | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [fileName, setFileName] = useState<string>("");
	const [hasGroundPlane, setHasGroundPlane] = useState<boolean>(false);
	const [groundPlaneScale, setGroundPlaneScale] = useState<number>(1);

	useEffect(() => {
		const container = containerRef.current;
		if (container === null) {
			return;
		}

		const scene = new THREE.Scene();
		scene.background = new THREE.Color(0x1a1a1a);

		const camera = new THREE.PerspectiveCamera(60, container.clientWidth / container.clientHeight, 0.1, 10000);
		camera.position.set(300, 300, 300);

		const renderer = new THREE.WebGLRenderer({ antialias: false });
		renderer.setSize(container.clientWidth, container.clientHeight);
		renderer.setPixelRatio(1);
		container.appendChild(renderer.domElement);

		const controls = new OrbitControls(camera, renderer.domElement);
		controls.target.set(0, 0, 0);

		scene.add(new THREE.AmbientLight(0xffffff, 0.6));
		const directional = new THREE.DirectionalLight(0xffffff, 0.8);
		directional.position.set(200, 400, 300);
		scene.add(directional);

		scene.add(new THREE.GridHelper(1000, 50, 0x444444, 0x222222));
		scene.add(new THREE.AxesHelper(50));

		const root = new THREE.Group();
		scene.add(root);

		stageRef.current = { root, camera, controls };

		const handleResize = (): void => {
			camera.aspect = container.clientWidth / container.clientHeight;
			camera.updateProjectionMatrix();
			renderer.setSize(container.clientWidth, container.clientHeight);
		};
		window.addEventListener("resize", handleResize);

		let animationId = 0;
		const loop = (): void => {
			animationId = requestAnimationFrame(loop);
			controls.update();
			renderer.render(scene, camera);
		};
		loop();

		return () => {
			cancelAnimationFrame(animationId);
			window.removeEventListener("resize", handleResize);
			controls.dispose();
			renderer.dispose();
			container.removeChild(renderer.domElement);
			stageRef.current = null;
		};
	}, []);

	useEffect(() => {
		const groundPlane = groundPlaneRef.current;
		if (groundPlane === null) {
			return;
		}
		groundPlane.scale.set(groundPlaneScale, 1, groundPlaneScale);
	}, [groundPlaneScale]);

	const handleFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
		const file = event.target.files?.[0];
		if (file === undefined) {
			return;
		}

		setError(null);
		setFileName(file.name);

		const reader = new FileReader();

		reader.onload = () => {
			try {
				const json = JSON.parse(String(reader.result)) as PayloadJson;
				if (stageRef.current === null) {
					return;
				}
				const groundPlane = buildScene(stageRef.current, json);
				groundPlaneRef.current = groundPlane;
				setHasGroundPlane(groundPlane !== null);
				setGroundPlaneScale(1);
			} catch (e) {
				setError(`Failed to parse JSON: ${String(e)}`);
			}
		};

		reader.readAsText(file);
	};

	return (
		<div style={{ position: "relative", width: "100vw", height: "100vh", background: "#1a1a1a" }}>
			<div ref={containerRef} style={{ position: "absolute", inset: 0 }} />
			<div
				style={{
					position: "absolute",
					top: 12,
					left: 12,
					padding: "8px 12px",
					background: "rgba(0, 0, 0, 0.6)",
					color: "#eee",
					fontFamily: "sans-serif",
					fontSize: 13,
					borderRadius: 6,
					display: "flex",
					flexDirection: "column",
					gap: 6,
				}}
			>
				<label style={{ cursor: "pointer" }}>
					<input type="file" accept="application/json,.json" onChange={handleFileChange} style={{ display: "none" }} />
					<span style={{ textDecoration: "underline" }}>Load JSON…</span>
				</label>
				<span style={{ opacity: 0.7 }}>Current: {fileName}</span>
				{error !== null && <span style={{ color: "#f44336" }}>{error}</span>}
				{hasGroundPlane && (
					<label style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 4 }}>
						<span style={{ opacity: 0.7 }}>Underlay scale: {groundPlaneScale.toFixed(2)}×</span>
						<input
							type="range"
							min={0.1}
							max={5}
							step={0.05}
							value={groundPlaneScale}
							onChange={(event) => setGroundPlaneScale(Number(event.target.value))}
							style={{ width: 220 }}
						/>
					</label>
				)}
			</div>
		</div>
	);
};

export { PayloadViewer };
